import React, { useState, useEffect } from "react";
import axios from "axios";
import { Settings, ShieldAlert, Save, RefreshCcw, Lock, Power, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const AdminSettings = () => {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMaintenance, setIsMaintenance] = useState(false);
  
  const [passwords, setPasswords] = useState({ current: "", new: "", confirm: "" });
  const [profile, setProfile] = useState({
    username: user?.username || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: user?.address || ""
  });

  // Token Handle
  const adminToken = user?.accessToken || user?.token;
  const config = { headers: { token: `Bearer ${adminToken}` } };

  // --- 1. Load Maintenance Status ---
 useEffect(() => {
  const getStatus = async () => {
    try {
      // Endpoint match kiya: /api/settings/status
      const res = await axios.get("https://wethrift-backend.onrender.com/api/settings/status", config);
      setIsMaintenance(res.data.status);
    } catch (err) {
      console.log("Status fetch failed - Ensure Admin Token is valid");
    }
  };
  if (adminToken) getStatus();
}, [adminToken]);

  // --- 2. Profile Update ---
 // AdminSettings.jsx mein "handleProfileUpdate" ko ise replace karein
const handleProfileUpdate = async (e) => {
  e.preventDefault();
  setLoading(true);
  try {
    if (!user?._id) return alert("❌ Session expired!");
    const res = await axios.put(`https://wethrift-backend.onrender.com/api/users/${user._id}`, profile, config);
    
    // Alert tabhi aayega jab response success hoga
    const updatedUser = { ...user, ...res.data };
    localStorage.setItem("weThriftUser", JSON.stringify(updatedUser));
    setUser(updatedUser); 
    alert("✅ Identity synchronized!");
  } catch (err) {
    alert(err.response?.data?.message || "Profile update failed!");
  } finally {
    setLoading(false);
  }
};

// AdminSettings.jsx mein "handleSecurityUpdate" ko ise replace karein
const handleSecurityUpdate = async (e) => {
  e.preventDefault();
  if (passwords.new !== passwords.confirm) return alert("❌ Passwords do not match!");
  
  setLoading(true);
  try {
    // Yahan "newPassword" bhej rahe hain jo ab backend accept karega
    await axios.put(`https://wethrift-backend.onrender.com/api/users/security/${user._id}`, {
      newPassword: passwords.new 
    }, config);

    alert("🔐 Password updated successfully!");
    setPasswords({ current: "", new: "", confirm: "" });
  } catch (err) {
    alert(err.response?.data?.message || "Security update failed!");
  } finally {
    setLoading(false);
  }
};

  // --- 4. Maintenance Toggle (Fixed) ---
const toggleMaintenance = async () => {
  const action = isMaintenance ? "Restore public access?" : "ACTIVATE MAINTENANCE MODE?";
  if (!window.confirm(action)) return;

  setLoading(true);
  try {
    // Backend ke hisaab se POST method aur /api/settings/toggle path
    const res = await axios.post("https://wethrift-backend.onrender.com/api/settings/toggle", 
      { status: !isMaintenance }, 
      config 
    );
    
    setIsMaintenance(res.data.status);
    alert(`System is now ${!isMaintenance ? "LOCKED" : "LIVE"}`);
  } catch (err) {
    console.error(err);
    alert("⚠️ Protocol override failed! Check Admin permissions.");
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-20 font-sans p-4">
      {/* Header */}
      <div className="flex items-center gap-4 border-b border-white/5 pb-8">
        <div className="p-4 bg-zinc-900 rounded-3xl border border-white/5">
          <Settings className="text-zinc-400" size={32} />
        </div>
        <div>
          <h1 className="text-3xl font-black tracking-tighter text-white uppercase italic">Command <span className="text-zinc-600">Center</span></h1>
          <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest">System Configuration & Security</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Profile */}
        <section className="bg-zinc-950 border border-white/5 p-8 rounded-[2.5rem] space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="text-emerald-500" size={18} />
            <h3 className="text-sm font-black uppercase text-white italic">Identity Profile</h3>
          </div>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <input type="text" value={profile.username} onChange={(e) => setProfile({...profile, username: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3 text-sm text-white focus:outline-none focus:border-emerald-500/50" placeholder="Username" />
            <input type="email" value={profile.email} onChange={(e) => setProfile({...profile, email: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3 text-sm text-white focus:outline-none focus:border-emerald-500/50" placeholder="Email" />
            <button disabled={loading} className="w-full bg-white text-black py-4 rounded-2xl text-[10px] font-black uppercase hover:bg-emerald-500 transition-all flex items-center justify-center gap-2">
              <Save size={14} /> {loading ? "Syncing..." : "Update Identity"}
            </button>
          </form>
        </section>

        {/* Security */}
        <section className="bg-zinc-950 border border-white/5 p-8 rounded-[2.5rem] space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="text-red-500" size={18} />
            <h3 className="text-sm font-black uppercase text-white italic">Security Protocol</h3>
          </div>
          <form onSubmit={handleSecurityUpdate} className="space-y-4">
            <input type="password" placeholder="Current Password" value={passwords.current} onChange={(e) => setPasswords({...passwords, current: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3 text-sm text-white focus:outline-none focus:border-red-500/50" />
            <input type="password" placeholder="New Secure Password" value={passwords.new} onChange={(e) => setPasswords({...passwords, new: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3 text-sm text-white focus:outline-none focus:border-red-500/50" />
            <button disabled={loading} className="w-full bg-zinc-900 text-white py-4 rounded-2xl text-[10px] font-black uppercase border border-white/10 hover:bg-red-500 transition-all flex items-center justify-center gap-2">
              <RefreshCcw size={14} className={loading ? "animate-spin" : ""} /> Update Security
            </button>
          </form>
        </section>
      </div>

      {/* Maintenance Shield */}
      <div className={`p-8 rounded-[2.5rem] flex flex-col md:flex-row items-center justify-between border transition-all ${isMaintenance ? "bg-emerald-500/5 border-emerald-500/20" : "bg-red-500/5 border-red-500/10"}`}>
        <div className="flex items-center gap-4 mb-4 md:mb-0">
          <ShieldAlert className={isMaintenance ? "text-emerald-500" : "text-red-500"} size={24} />
          <div>
            <h4 className={`text-[10px] font-black uppercase ${isMaintenance ? "text-emerald-500" : "text-red-500"}`}>Maintenance Shield</h4>
            <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-tight">Public access is {isMaintenance ? "LOCKED (HIDDEN)" : "LIVE (PUBLIC)"}</p>
          </div>
        </div>
        <button onClick={toggleMaintenance} disabled={loading} className={`px-8 py-3 rounded-full text-[10px] font-black uppercase transition-all flex items-center gap-2 ${isMaintenance ? "bg-emerald-500 text-black shadow-lg shadow-emerald-500/20" : "bg-red-500 text-white shadow-lg shadow-red-500/20"}`}>
          <Power size={14} /> {isMaintenance ? "Deactivate" : "Activate"}
        </button>
      </div>
    </div>
  );
};

export default AdminSettings;